/**
 * feedReceipts — turn a Feed message's `refs` (unknown JSON the agents attach as
 * proof) into what a founder should actually SEE, not developer plumbing.
 *
 * Before this, the Feed dumped every ref as `key: value` pairs, so a founder
 * read lines like `task_id: task_cf6ff… · r2_keys: ["evidence/leo/…png", …]` —
 * an internal UUID plus a raw JSON array of storage paths, which also BURIED the
 * real proof those paths point to (screenshots). This module:
 *   • drops internal plumbing IDs (task_id, standup_id, incident_id, …) — a
 *     founder never needs a UUID; the row already shows who/what/when;
 *   • pulls out evidence image keys so the UI can render them as tappable
 *     screenshot receipts (the Dashboard's "trust the receipts" pattern);
 *   • keeps any remaining MEANINGFUL fact as one plain line, and NEVER emits
 *     raw JSON (non-scalar leftovers are dropped, not stringified).
 *
 * Kept dependency-free so it is unit-tested directly (tests/feed-receipts.test.ts).
 */

/** Ref keys that are internal plumbing — a founder never needs to see a UUID. */
const HIDDEN_KEYS = new Set([
  'task_id',
  'standup_id',
  'incident_id',
  'run_id',
  'run',
  'session_id',
  'sessionid',
  'message_id',
  'thread_id',
]);

const IMG_RE = /\.(png|jpe?g|gif|webp|avif|svg)$/i;

/** An R2 asset key we can resolve + show as a screenshot receipt. */
function isImageKey(v: unknown): v is string {
  return typeof v === 'string' && (IMG_RE.test(v) || v.startsWith('evidence/'));
}

function isScalar(v: unknown): v is string | number | boolean {
  return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
}

/** "high" for `severity` reads best as "high severity"; everything else as
 *  "label: value" with underscores turned to spaces. Purely presentational. */
function fact(key: string, value: string | number | boolean): string {
  const v = String(value).trim();
  if (!v) return '';
  if (key === 'severity') return `${v} severity`;
  const label = key.replace(/_/g, ' ');
  return `${label}: ${v}`;
}

export interface FeedReceipts {
  /** R2 keys to render as tappable screenshot thumbnails. */
  images: string[];
  /** One plain line of any remaining meaningful facts, or null. */
  facts: string | null;
}

const EMPTY: FeedReceipts = { images: [], facts: null };

/**
 * Parse a message's `refs` into founder-facing receipts. Accepts the object,
 * array, or (JSON- or plain-) string shapes agents actually attach; anything it
 * can't turn into a screenshot or a meaningful scalar fact is dropped.
 */
export function parseReceipts(refs: unknown): FeedReceipts {
  if (refs == null || refs === '') return EMPTY;

  // Strings: try JSON, else treat the whole thing as one plain fact line.
  if (typeof refs === 'string') {
    const s = refs.trim();
    if (!s) return EMPTY;
    if (s.startsWith('{') || s.startsWith('[')) {
      try {
        return parseReceipts(JSON.parse(s));
      } catch {
        /* not JSON after all — fall through to plain text */
      }
    }
    return isImageKey(s) ? { images: [s], facts: null } : { images: [], facts: s };
  }

  const images: string[] = [];
  const facts: string[] = [];
  const pushImages = (arr: unknown[]) => {
    for (const x of arr) if (isImageKey(x)) images.push(x);
  };

  if (Array.isArray(refs)) {
    pushImages(refs);
    for (const x of refs) {
      if (isImageKey(x)) continue;
      if (isScalar(x)) {
        const f = String(x).trim();
        if (f) facts.push(f);
      } else if (x && typeof x === 'object') {
        // Pull only a human label from an object entry; never dump the object.
        const o = x as Record<string, unknown>;
        for (const k of ['label', 'title', 'name', 'url']) {
          if (isScalar(o[k])) {
            const f = String(o[k]).trim();
            if (f) facts.push(f);
            break;
          }
        }
      }
    }
    return { images, facts: facts.length ? facts.join('  ·  ') : null };
  }

  if (typeof refs === 'object') {
    for (const [k, v] of Object.entries(refs as Record<string, unknown>)) {
      if (Array.isArray(v)) {
        pushImages(v);
        continue;
      }
      if (isImageKey(v)) {
        images.push(v);
        continue;
      }
      if (HIDDEN_KEYS.has(k)) continue; // internal plumbing UUID — drop it
      if (isScalar(v)) {
        const f = fact(k, v);
        if (f) facts.push(f);
      }
      // else: a nested object/other — drop it rather than emit raw JSON
    }
    return { images, facts: facts.length ? facts.join('  ·  ') : null };
  }

  // number/boolean at top level
  return isScalar(refs) ? { images: [], facts: String(refs) } : EMPTY;
}
